import { defaultShouldDehydrateQuery, QueryClient } from '@tanstack/react-query';
import { type PersistQueryClientOptions, persistQueryClientRestore } from '@tanstack/react-query-persist-client';
import { ResponseError } from '@/models/response';
import { createQueryPersister } from '@/services/storage-manager';
import { addTranslateToast } from '@/stores/toast';
import { APP_VERSION_HASH } from '@/utils/constants';

export const RETRY_DELAYS: Readonly<Record<number, number>> = Object.freeze({
  0: 1000,
  1: 1000,
  2: 2000,
  3: 3000,
  4: 5000,
  5: 8000,
});

export function isRetryEnabledError(error: Error): boolean {
  if (ResponseError.isResponseError(error)) return false;

  return error.message !== 'NOT_AUTHENTICATED';
}

function isThrowableError(error: Error): boolean {
  if (error.message === 'Failed to fetch') return false;

  return isRetryEnabledError(error);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      staleTime: 1000 * 60, // 1 minute
      gcTime: 1000 * 60 * 60 * 24, // 1 day
      throwOnError: isThrowableError,
      retry: (failureCount, error) => isRetryEnabledError(error) && failureCount <= 5,
      retryDelay: (failureCount) => RETRY_DELAYS[failureCount] ?? 10000,
      experimental_prefetchInRender: true,
    },
    mutations: {
      networkMode: 'always',
      throwOnError: false,
      retry: (failureCount, error) => isRetryEnabledError(error) && failureCount <= 3,
      retryDelay: (failureCount) => RETRY_DELAYS[failureCount] ?? 10000,
      onError: (e) =>
        addTranslateToast({
          type: 'error',
          message: e.message || 'common.submit-failed',
        }),
    },
  },
});

export function createPersistQueryClientOptions() {
  const persister = createQueryPersister();

  const persistQueryClientOptions: PersistQueryClientOptions = {
    queryClient,
    persister,
    buster: APP_VERSION_HASH,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) =>
        defaultShouldDehydrateQuery(query) && !query.queryHash.match(/GetChatData|GetGroupChatData/gi),
    },
  };

  const restoreQueryClientPersistence = () =>
    persistQueryClientRestore({
      ...persistQueryClientOptions,
      maxAge: 1000 * 60 * 60 * 24,
    });

  return [persistQueryClientOptions, restoreQueryClientPersistence] as const;
}
